import { get } from '../shared/utils/FetchV2.util';
import { logger } from '../shared/utils/logger';
import { getStorage } from '../shared/utils/general.util';
import LOCAL_STORAGE_KEY from '../shared/constants/localStorageKey';
import { SubscriptionType } from '../@types/Subscription';
import { Acquisitions } from '../@types/Acquisitions.type';

/**
 * Sales Overview — combined subscriptions + acquisitions from
 * heru-sales-transaction-engine in ONE call:
 *   GET /api/v1/heru-sales-transaction-engine/user/sales-overview
 *
 * Replaces the two legacy bootstrap calls (GET /sales/subscriptions?all=true +
 * GET /sales/plans/acquisitions). The backend resolves the user from the Bearer
 * JWT (FetchV2 attaches it), so no user_id is sent.
 *
 * This module is a COMPAT ADAPTER: the engine returns a clean, lean, redesigned
 * shape, and here we map it back to the legacy `SubscriptionType` / `Acquisitions`
 * shapes the existing reducers and components already consume. This keeps the
 * blast radius to the service layer — no component, slice or type changes — while
 * the app already benefits from the better backend (recovered legacy prices,
 * fixed card details, standardized formats, fewer 5xx). Consumers can be
 * progressively migrated to the lean shape later.
 */

// ---- Lean response shape from the engine ----
interface LeanProduct {
	id: number | null;
	name: string | null;
	slug: string | null;
	regime: string[];
	default_product_version_id?: number | null;
}

interface LeanPaymentMethod {
	type: 'card' | 'spei' | 'cash' | 'paypal' | 'unknown';
	brand?: string;
	last_four?: string;
	expires?: string;
}

interface LeanSubscription {
	id: number;
	status: 'active' | 'failed_renewal' | 'non_renewing' | 'expired' | 'cancelled';
	active: boolean;
	renew_at_period_end: boolean;
	payment_status: string | null;
	failure_reason: string | null;
	shopping_cart_id: number | null;
	current_period_start: string | null;
	current_period_end: string | null;
	created_at: string;
	paused_until?: string | null;
	pause_indefinite?: boolean;
	coupon_code?: string | null;
	coupon_percent_off?: number | null;
	periodicity_days: number | null;
	product_version_id: number | null;
	price: number | null;
	product: LeanProduct;
	payment_method: LeanPaymentMethod;
	billing_platform: 'stripe' | 'legacy';
}

interface LeanAcquisition {
	id: number;
	created_at: string;
	total: number | null;
	shopping_cart_id: number | null;
	year: number | null;
	product: LeanProduct;
	payment_method: LeanPaymentMethod;
}

interface SalesOverviewResponse {
	subscriptions: LeanSubscription[];
	acquisitions: LeanAcquisition[];
	errors: Record<string, string>;
}

export interface SalesOverview {
	subscriptions: SubscriptionType[];
	acquisitions: Acquisitions[];
	errors: Record<string, string>;
}

// payment_method.type (lean) → the legacy acquisition payment_method string that
// PurchaseRow.renderPaymentMethod switches on.
const ACQ_PAYMENT_LABEL: Record<string, string> = {
	card: 'CARD',
	cash: 'CASH',
	spei: 'SPEI',
	paypal: 'Paypal',
};

export const mapSubscription = (s: LeanSubscription): SubscriptionType =>
	({
		id: s.id,
		user_id: 0,
		created_at: s.created_at,
		// Period fields arrive as "YYYY-MM-DD"; `new Date(...)` parses them fine.
		current_period_start: s.current_period_start ?? '',
		current_period_end: s.current_period_end ?? '',
		renew_at_period_end: s.renew_at_period_end,
		active: s.active,
		payment_status: s.payment_status ?? undefined,
		failure_reason: s.failure_reason ?? '',
		additional_information: {
			pause:
				s.paused_until || s.pause_indefinite
					? { until: s.paused_until ?? '', indefinite: s.pause_indefinite }
					: undefined,
		},
		// SubCardCompact reads last_transaction.shopping_cart_id for the SPEI
		// pending-order detail; keep that working.
		last_transaction: s.shopping_cart_id != null
			? { shopping_cart_id: s.shopping_cart_id }
			: undefined,
		coupon: s.coupon_code
			? { code: s.coupon_code, percent_off: s.coupon_percent_off ?? 0 }
			: undefined,
		payment_type: s.payment_method.type,
		collection_method: s.payment_method.type === 'spei' ? 'spei' : 'card',
		subscription_treatment:
			s.billing_platform === 'stripe' ? 'HCP_STRIPE_PLATFORM' : 'NORMAL',
		payment_details: {
			brand: s.payment_method.brand ?? '',
			last_four: s.payment_method.last_four ?? '',
			expires: s.payment_method.expires ?? '',
			origin: '',
			card_type: '',
		},
		product_version: {
			id: s.product_version_id ?? 0,
			product_id: s.product.id ?? 0,
			currency_id: 1,
			price: s.price ?? 0,
			periodicity: s.periodicity_days ?? 0,
			description: '',
			created_at: '',
			product: {
				id: s.product.id ?? 0,
				name: s.product.name ?? '',
				slug: s.product.slug ?? '',
				regime: s.product.regime ?? [],
			},
		},
		// Server-computed status, exposed for consumers that want to drop their
		// own derivation later.
		status: s.status,
	} as unknown as SubscriptionType);

const mapAcquisition = (a: LeanAcquisition): Acquisitions => {
	const pm = a.payment_method;
	const isCard = pm.type === 'card';
	const hasMethod = pm.type !== 'unknown';
	return {
		id: a.id,
		user_id: 0,
		product: {
			id: a.product.id ?? 0,
			name: a.product.name ?? '',
			regime: a.product.regime ?? [],
			default_product_version_id: a.product.default_product_version_id ?? 0,
		},
		additional_information: { year: a.year ?? undefined },
		coupon: {},
		shopping_item: {
			// PurchaseRow: total>0 → price, ===0 → "Cortesía", falsy → "No disponible".
			total: a.total ?? undefined,
			shopping_cart: { id: a.shopping_cart_id ?? 0 },
		},
		created_at: a.created_at,
		payment_method: ACQ_PAYMENT_LABEL[pm.type] ?? '',
		// PurchaseRow renders the payment chip only when last_transaction is
		// truthy; keep that: present for known methods, absent for unknown.
		last_transaction: hasMethod
			? {
					token_brand: pm.brand ?? '',
					token_digits: pm.last_four ?? '',
					expires_at: pm.expires ?? '',
			  }
			: undefined,
		payment_details: isCard
			? {
					brand: pm.brand ?? '',
					last_four: pm.last_four ?? '',
					expires: pm.expires ?? '',
			  }
			: undefined,
	} as unknown as Acquisitions;
};

const fetchAndMap = async (): Promise<SalesOverview> => {
	const res = await get<SalesOverviewResponse>({
		service: 'salesTransactionEngine',
		endpoint: '/user/sales-overview',
	});
	const errors = res?.errors ?? {};
	if (Object.keys(errors).length > 0) {
		logger.error('sales-overview partial failure', errors);
	}
	return {
		subscriptions: (res?.subscriptions ?? []).map(mapSubscription),
		acquisitions: (res?.acquisitions ?? []).map(mapAcquisition),
		errors,
	};
};

// In-flight coalescing: at bootstrap both the subscriptions and acquisitions
// queries fire together; sharing the pending promise means a SINGLE network call
// instead of two. Keyed by the active token so an in-place token swap
// (impersonation / `?token=` deeplink) never hands one user's promise to
// another. Cleared once it settles (no stale caching).
let inflight: { token: string; promise: Promise<SalesOverview> } | null = null;

export const fetchSalesOverview = (): Promise<SalesOverview> => {
	const token = getStorage().getItem(LOCAL_STORAGE_KEY.TOKEN) ?? '';
	if (!inflight || inflight.token !== token) {
		const promise = fetchAndMap().finally(() => {
			if (inflight?.token === token) {
				inflight = null;
			}
		});
		inflight = { token, promise };
	}
	return inflight.promise;
};
